import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';

import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import { Header, HeaderTitle } from 'components/Header';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';

import {
    GetMealPlanDefinitionsDocument,
    useCreateMealPlanDefinitionMutation,
    TotadminPermission,
} from 'data/__generated__';

import { EligibleCategoriesSection } from './components/EligibleCategoriesSection';
import { GeneralInfoSection } from './components/GeneralInfoSection';
import { ImagesSection } from './components/ImagesSection';
import { EMPTY_MEAL_PLAN_DEFINITION, toCreateInput, type MealPlanDefinitionFormData } from './constants/formData';

const ACTION_GROUPS: PageActionGroup[] = [
    {
        label: 'Navigation',
        items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: PAGES.mealPlanDefinitions.url }],
    },
];

export const MealPlanDefinitionCreate = () => {
    const navigate = useNavigate();

    // Les listes du formulaire passent par `useFieldArray`, qui exige que leur valeur initiale soit
    // déclarée ici et pas sur les champs.
    const methods = useForm<MealPlanDefinitionFormData>({ defaultValues: EMPTY_MEAL_PLAN_DEFINITION });

    const [createMealPlanDefinition, { loading }] = useCreateMealPlanDefinitionMutation({
        refetchQueries: [GetMealPlanDefinitionsDocument],
    });

    const onSubmit = methods.handleSubmit(
        async (values) => {
            try {
                const { data } = await createMealPlanDefinition({ variables: { input: toCreateInput(values) } });

                if (!data?.createMealPlanDefinition) {
                    throw new Error("Une erreur inconnue s'est produite");
                }

                toast.success('La formule a bien été créée !');
                navigate(`/meal-plan-definition/${data.createMealPlanDefinition._id}`);
            } catch (error) {
                toast.error(error instanceof Error ? error.message : 'La formule n’a pas pu être créée', {
                    autoClose: false,
                });
            }
        },
        () => toast.error("Le formulaire n'est pas valide, vérifiez les champs en rouge"),
    );

    return (
        <Container>
            <FormProvider {...methods}>
                <Form onSubmit={onSubmit}>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.mealPlanDefinitionCreate} />
                        </HeaderTitle>
                        <ActionsAndSave
                            actionGroups={ACTION_GROUPS}
                            onSave={onSubmit}
                            saveLoading={loading}
                            saveTitle="Créer"
                            saveRequiredPermissions={[TotadminPermission.MealPlanDefinitionsManage]}
                        />
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <GeneralInfoSection />
                            <EligibleCategoriesSection />
                            <ImagesSection />
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;
